import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Avatar,
  Typography,
  Box,
  Chip,
  Grid,
  Checkbox,
  Button,
  CircularProgress
} from '@mui/material';
import {
  Storage as StorageIcon,
  Monitor as MonitorIcon,
  Delete as DeleteIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ServerCard = ({
  server,
  isSelected,
  isDeleting,
  hasPendingOperation,
  onSelect,
  onMonitor,
  onDelete,
  onRefresh
}) => {
  const navigate = useNavigate();

  const handleMonitorClick = () => {
    if (server.isRegistering || server.isRegistrationFailed) return;
    if (onMonitor) {
      onMonitor(server);
    } else {
      navigate(`/monitoring/${server.nodeId || server.id}`);
    }
  };

  const handleDeleteClick = async () => {
    if (server.isRegistering || isDeleting) return;
    const confirmMessage = server.isRegistrationFailed 
      ? `Are you sure you want to delete the failed node "${server.hostname}"?`
      : `Are you sure you want to delete node "${server.hostname}"?`;
    
    if (onDelete && window.confirm(confirmMessage)) {
      await onDelete(server.id);
      if (onRefresh) {
        await onRefresh();
      }
    }
  };

  const getServerTypeColor = (type) => {
    switch (type) {
      case 'High Performance':
        return { bg: 'rgba(76, 175, 80, 0.1)', color: '#4caf50' };
      case 'Compute':
        return { bg: 'rgba(33, 150, 243, 0.1)', color: '#2196f3' };
      case 'Storage':
        return { bg: 'rgba(156, 39, 176, 0.1)', color: '#9c27b0' };
      default:
        return { bg: 'rgba(158, 158, 158, 0.1)', color: '#9e9e9e' };
    }
  };

  const getStatusChipProps = (status, isRegistering, isRegistrationFailed) => {
    if (isRegistering) {
      return { 
        color: 'warning',
        icon: <CircularProgress size={12} sx={{ color: 'inherit' }} />
      };
    }
    if (isRegistrationFailed) {
      return {
        color: 'error',
        icon: <ErrorIcon sx={{ fontSize: '12px' }} />
      };
    }
    return {
      color: status === "Active" ? "success" : "error"
    };
  };

  const getCardBorderColor = () => {
    if (isSelected) return '2px solid #1976d2';
    if (isDeleting) return '2px solid #f44336';
    if (server.isRegistering) return '2px solid #ff9800';
    if (server.isRegistrationFailed) return '2px solid #f44336';
    return '1px solid rgba(255, 255, 255, 0.1)';
  };

  const getCardOpacity = () => {
    if (isDeleting) return 0.6;
    if (server.isRegistering) return 0.8;
    if (server.isRegistrationFailed) return 0.9;
    return 1;
  };

  const typeColors = getServerTypeColor(server.type);

  return (
    <Card 
      sx={{ 
        height: '100%', 
        backgroundColor: '#1a1a1a',
        borderRadius: '12px',
        border: getCardBorderColor(),
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          cursor: 'pointer',
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)'
        },
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'visible',
        opacity: getCardOpacity()
      }}
    >
      {server.isRegistering && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: 'warning.main',
            color: 'warning.contrastText',
            px: 1,
            py: 0.5,
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: 500,
            zIndex: 1
          }}
        >
          <HourglassEmptyIcon sx={{ fontSize: '0.875rem' }} />
          Setting up...
        </Box>
      )}

      {isDeleting && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: 'error.main',
            color: 'error.contrastText',
            px: 1,
            py: 0.5,
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: 500,
            zIndex: 1
          }}
        >
          <CircularProgress size={12} sx={{ color: 'inherit' }} />
          Deleting...
        </Box>
      )}

      <CardHeader 
        sx={{
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          pb: 2,
          '& .MuiCardHeader-content': { width: '100%' }
        }}
        title={
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar 
                sx={{ 
                  bgcolor: server.isRegistering ? 'warning.main' : 
                          server.isRegistrationFailed ? 'error.main' : 'primary.dark',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
                }}
              >
                {server.isRegistering ? <HourglassEmptyIcon /> : 
                 server.isRegistrationFailed ? <ErrorIcon /> : <StorageIcon />}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                  {server.hostname}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {server.location}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Health
                </Typography>
                <Chip 
                  label={server.isRegistering ? 'Setting up' : 
                         server.isRegistrationFailed ? 'Failed' : server.health}
                  color={
                    server.isRegistering ? 'warning' :
                    server.isRegistrationFailed ? 'error' :
                    server.health === 'Good' ? 'success' :
                    server.health === 'Moderate' ? 'warning' :
                    'error'
                  }
                  size="small"
                  sx={{ 
                    fontWeight: 500,
                    minWidth: 70,
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Chip 
                  label={server.status} 
                  {...getStatusChipProps(server.status, server.isRegistering, server.isRegistrationFailed)}
                  size="small"
                  sx={{ 
                    fontWeight: 500,
                    minWidth: 70,
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                />
              </Box>
              <Checkbox
                checked={isSelected}
                onChange={() => onSelect(server.id)}
                disabled={server.isRegistering || isDeleting}
                sx={{ 
                  color: 'primary.main',
                  p: 0.5,
                }}
              />
            </Box>
          </Box>
        }
      />
      <CardContent sx={{ pt: 2, pb: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              mb: 2,
              p: 1.5, 
              bgcolor: 'rgba(0, 0, 0, 0.2)', 
              borderRadius: 1,
              border: '1px solid rgba(255, 255, 255, 0.05)'
            }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  IP Address
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {server.ipAddress}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Uptime
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {server.isRegistering || server.isRegistrationFailed ? 'N/A' : server.uptime}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ 
              p: 1.5, 
              bgcolor: 'rgba(0, 0, 0, 0.2)', 
              borderRadius: 1,
              border: '1px solid rgba(255, 255, 255, 0.05)'
            }}>
              <Typography 
                variant="subtitle2" 
                color="primary" 
                gutterBottom 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  fontWeight: 600
                }}
              >
                System Configuration
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      CPU
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      {server.isRegistering ? 'Detecting...' : 
                       server.isRegistrationFailed ? 'Failed to detect' : server.cpuModel}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {server.isRegistering ? 'Please wait...' : 
                       server.isRegistrationFailed ? 'Registration failed' : 
                       `${server.cpuCores} cores, ${server.cpuThreads} threads @ ${server.clockSpeed}`}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Memory
                    </Typography>
                    <Typography variant="body2">
                      {server.isRegistering || server.isRegistrationFailed ? 'N/A' : `${server.memory} GB`}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Storage
                    </Typography>
                    <Typography variant="body2">
                      {server.isRegistering || server.isRegistrationFailed ? 'N/A' : `${server.storage} GB`}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Architecture
                    </Typography>
                    <Typography variant="body2">
                      {server.isRegistering ? 'Detecting...' : 
                       server.isRegistrationFailed ? 'Unknown' : server.architecture}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      OS
                    </Typography>
                    <Typography variant="body2">
                      {server.isRegistering ? 'Detecting...' : 
                       server.isRegistrationFailed ? 'Unknown' : `${server.os} ${server.osVersion}`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
      <CardActions 
        sx={{ 
          justifyContent: 'space-between', 
          p: 2,
          pt: 1,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Chip
          label={server.type}
          size="small"
          sx={{
            backgroundColor: typeColors.bg,
            color: typeColors.color,
            fontWeight: 500
          }}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            size="small" 
            startIcon={<MonitorIcon />}
            onClick={handleMonitorClick}
            disabled={server.isRegistering || server.isRegistrationFailed}
            sx={{
              bgcolor: (server.isRegistering || server.isRegistrationFailed) ? 'rgba(158, 158, 158, 0.1)' : 'rgba(255, 255, 255, 0.05)',
              '&:hover': { 
                bgcolor: (server.isRegistering || server.isRegistrationFailed) ? 'rgba(158, 158, 158, 0.1)' : 'rgba(255, 255, 255, 0.1)' 
              },
              '&:disabled': {
                color: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            Monitor
          </Button>
          <Button 
            size="small" 
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
            disabled={server.isRegistering}
            sx={{
              bgcolor: server.isRegistering ? 'rgba(158, 158, 158, 0.1)' : 
                       server.isRegistrationFailed ? 'rgba(244, 67, 54, 0.1)' : 
                       'rgba(255, 255, 255, 0.05)',
              '&:hover': { 
                bgcolor: server.isRegistering ? 'rgba(158, 158, 158, 0.1)' : 
                         server.isRegistrationFailed ? 'rgba(244, 67, 54, 0.2)' : 
                         'rgba(255, 255, 255, 0.1)' 
              },
              '&:disabled': {
                color: 'rgba(255, 255, 255, 0.3)'
              },
              color: server.isRegistrationFailed ? 'error.main' : 'inherit'
            }}
          >
            Delete
          </Button>
        </Box>
      </CardActions>
    </Card>
  );
};

export default ServerCard; 